import { appRelease, clientSentryDsn, SENTRY_ENVIRONMENT } from "./sentry-config";

let started = false;
let earlyErrorsHooked = false;
type PendingError = { error: unknown; context?: Record<string, unknown> };
const pending: PendingError[] = [];

/**
 * Catches errors that happen before the Sentry SDK chunk finishes loading
 * (early hydration, module evaluation, sync render crashes). Runs at module
 * scope so nothing in the pre-hydration window is lost.
 */
function hookEarlyErrors() {
  if (earlyErrorsHooked || typeof window === "undefined") return;
  earlyErrorsHooked = true;
  hookConsoleErrors();
  window.addEventListener("error", (event) => {
    pending.push({
      error: event.error ?? new Error(event.message),
      context: { source: "window.onerror", filename: event.filename },
    });
    void flushPending();
  });
  window.addEventListener("unhandledrejection", (event) => {
    pending.push({ error: event.reason, context: { source: "unhandledrejection" } });
    void flushPending();
  });
}

/**
 * Mirrors console.error / console.warn into Sentry breadcrumbs so a crash
 * report carries the warnings that led up to it, without sending extra events
 * (breadcrumbs are free; they ride along on the error that is already sent).
 */
function hookConsoleErrors() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __t1dwConsoleHooked?: boolean };
  if (w.__t1dwConsoleHooked) return;
  w.__t1dwConsoleHooked = true;

  (["error", "warn"] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      const message = args
        .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : String(a)))
        .join(" ")
        .slice(0, 500);
      void addClientBreadcrumb(message, undefined, `console.${level}`);
    };
  });
}

async function flushPending() {
  if (!pending.length) return;
  if (!clientSentryDsn()) {
    pending.length = 0;
    return;
  }
  await initSentryClient();
  const Sentry = await import("@sentry/react");
  while (pending.length) {
    const item = pending.shift()!;
    Sentry.captureException(item.error, item.context ? { extra: item.context } : undefined);
  }
}

/**
 * Reports resource-load failures (script / link / img). These fire on the
 * capture phase only and are the signal for stale chunks after a deploy.
 */
function hookResourceErrors() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __t1dwResourceHooked?: boolean };
  if (w.__t1dwResourceHooked) return;
  w.__t1dwResourceHooked = true;
  window.addEventListener(
    "error",
    (event) => {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
      if (!target || target === (window as unknown as HTMLElement)) return;
      const tag = target.tagName?.toLowerCase();
      if (tag !== "script" && tag !== "link" && tag !== "img") return;
      const url = target.src ?? target.href ?? "";
      void captureClientError(new Error(`Resource failed to load: ${tag} ${url}`), {
        surface: "resource_load",
        tag,
        url,
      });
    },
    true,
  );
}

/**
 * Wraps window.fetch so backend 4xx/5xx, network failures and slow calls are
 * reported without any call site remembering to do it.
 */
const SLOW_REQUEST_MS = 8_000;
function hookFetch() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __t1dwFetchHooked?: boolean };
  if (w.__t1dwFetchHooked) return;
  w.__t1dwFetchHooked = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const startedAt = Date.now();
    const sameOrigin = url.startsWith("/") || url.startsWith(window.location.origin);
    try {
      const response = await originalFetch(input as RequestInfo, init);
      const duration = Date.now() - startedAt;
      if (sameOrigin && !url.includes("/api/public/report-client-error")) {
        // The preview dev server briefly answers 502/503/504 while it reloads
        // after an edit. That is tooling noise, not an app fault.
        // A 404 on /_serverFn in dev means the open tab still holds the previous
        // build's server-function id after a rebuild; the next reload fixes it.
        const transientDevGateway =
          import.meta.env.DEV &&
          ([502, 503, 504].includes(response.status) ||
            (response.status === 404 && url.includes("/_serverFn")));

        // A server function answering 401/403 is an auth-state transition (a
        // token that expired between tabs, a refresh in flight). The auth retry
        // middleware re-runs the call and the route gate redirects when access
        // really is denied, so record a breadcrumb instead of an error.
        const authTransition =
          [401, 403].includes(response.status) && url.includes("/_serverFn");
        if (authTransition) {
          void addClientBreadcrumb(
            `auth ${response.status} ${url}`,
            { url, status: response.status },
            "fetch",
          );
        } else if (response.status >= 400 && !transientDevGateway) {
          void captureClientError(new Error(`HTTP ${response.status} ${url}`), {
            surface: "fetch",
            status: response.status,
            url,
            duration_ms: duration,
          });
        } else if (duration > SLOW_REQUEST_MS) {
          void addClientBreadcrumb(`slow request ${duration}ms ${url}`, { url, duration }, "fetch");
        }
      }
      return response;
    } catch (error) {
      if (sameOrigin) {
        void captureClientError(error, { surface: "fetch", url, kind: "network_failure" });
      }
      throw error;
    }
  };
}

/** Browser-only Sentry init. Safe to call multiple times. */
export async function initSentryClient() {
  hookEarlyErrors();
  hookResourceErrors();
  hookFetch();
  if (started || typeof window === "undefined") return;
  const dsn = clientSentryDsn();
  if (!dsn) return;
  started = true;

  const Sentry = await import("@sentry/react");
  const { scrubEvent, scrubBreadcrumb } = await import("./sentry-scrub");
  Sentry.init({
    dsn,
    environment: SENTRY_ENVIRONMENT,
    release: appRelease(),
    // Keep the free-plan event budget intact: light tracing, no replay sampling
    // outside errors, and a generous breadcrumb trail (breadcrumbs are free).
    maxBreadcrumbs: 100,
    initialScope: { tags: { runtime: "browser" } },
    sendDefaultPii: false,
    attachStacktrace: true,
    tracesSampleRate: 0.05,
    tracePropagationTargets: [/^\//, window.location.origin],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    integrations: (defaults) => [
      ...defaults,
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
      Sentry.captureConsoleIntegration({ levels: ["error", "warn"] }),
      Sentry.extraErrorDataIntegration({ depth: 4 }),
      Sentry.dedupeIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
      }),
    ],
    // Quota is finite on the free plan, so drop the classes of noise that are
    // never actionable: browser extensions, flaky mobile connections, aborted
    // navigations and third-party script chatter.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "AbortError",
      "The operation was aborted",
      "Non-Error promise rejection captured",
      "Failed to fetch",
      "NetworkError when attempting to fetch resource",
      "Load failed",
      "Importing a module script failed",
      "Failed to fetch dynamically imported module",
      "ChunkLoadError",
      "Loading chunk",
      "Loading CSS chunk",
      "top.GLOBALS",
      "ResizeObserver",
      // Lovable editor preview chatter: the injected editor bridge warns about
      // postMessage types it does not know. Never an app defect.
      "Unknown message type",
      // Sentry's own replay recorder warns when it cannot read a cross-origin
      // stylesheet. Recorder chatter, never an app defect.
      "Cannot get CSS styles from text's parentNode",
      /cssRules getter/i,
      // Stale server function ids and Vite transform errors are dev tooling.
      "Invalid server function ID",
      "[vite] Internal Server Error",
      /^Java(script)? exception/i,
      /extension context invalidated/i,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
      /connect\.facebook\.net/i,
      /static\.hotjar\.com/i,
      /\/@vite\/client/i,
      // Lovable editor runtime, only present inside the preview iframe.
      /\/lovable\.js/i,
    ],

    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb as Record<string, unknown>) as typeof crumb,
    beforeSend: (event, hint) => {
      if (!shouldSendEvent(event, hint)) return null;
      const scrubbed = scrubEvent(event as unknown as Record<string, unknown>);
      queueFallbackReport(scrubbed);
      return scrubbed as unknown as typeof event;
    },
  });
}

// --- Ad-block fallback --------------------------------------------------
// 5-15% of visitors block *.ingest.sentry.io. We remember the last event we
// handed to the transport; if no successful send is observed shortly after, a
// scrubbed summary is posted to our own origin and re-emitted server-side.
let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
let ingestReachable: boolean | undefined;

function queueFallbackReport(event: Record<string, unknown>) {
  if (typeof window === "undefined" || ingestReachable === true) return;
  const exception = (
    event["exception"] as { values?: Array<Record<string, unknown>> } | undefined
  )?.values?.[0];
  const summary = {
    name: String(exception?.["type"] ?? "ClientError"),
    message: String(exception?.["value"] ?? event["message"] ?? "Client error"),
    stack: JSON.stringify(exception?.["stacktrace"] ?? {}).slice(0, 4_000),
    url: window.location.href,
    tags: event["tags"] as Record<string, string> | undefined,
    extra: event["extra"] as Record<string, unknown> | undefined,
  };
  clearTimeout(fallbackTimer);
  fallbackTimer = setTimeout(async () => {
    if (ingestReachable === undefined) ingestReachable = await probeIngest();
    if (ingestReachable) return;
    try {
      await fetch("/api/public/report-client-error", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(summary),
        keepalive: true,
      });
    } catch {
      // Nothing else we can do.
    }
  }, 1_500);
}

/** Cheap reachability probe for the Sentry ingest host. */
async function probeIngest(): Promise<boolean> {
  try {
    const host = new URL(clientSentryDsn()).origin;
    await fetch(`${host}/api/0/`, { method: "HEAD", mode: "no-cors", cache: "no-store" });
    return true;
  } catch {
    return false;
  }
}


// --- Client-side dedupe + rate cap -------------------------------------
// Two guards keep a single looping component from burning the whole event
// budget: identical signatures are sent once per session, and the browser
// sends at most MAX_EVENTS_PER_WINDOW events per rolling window.
const MAX_EVENTS_PER_WINDOW = 12;
const WINDOW_MS = 60_000;
const seenSignatures = new Set<string>();
let windowStart = 0;
let windowCount = 0;

/**
 * Critical-path bypass: failures in the core pipeline (claiming a spot, sending
 * outreach email) are always sent, even when the rate cap has been hit.
 */
let criticalInFlight = 0;

/**
 * Expected auth control flow, not defects: a protected server function called
 * while the browser has no session (sign-out, expired token, an authenticated
 * query still mounted during the redirect to /auth) throws Unauthorized by
 * design. The router already sends those people to /auth.
 */
const EXPECTED_AUTH_ERRORS =
  /Unauthorized: No authorization header provided|^Unauthorized$|Admin access is required/i;

/**
 * Validation messages written for the person using the app. A server function
 * throws these as UserFacingError, but the class name does not survive the
 * serialized round trip to the browser, so they are matched on the message.
 */
export const EXPECTED_USER_ERRORS =
  /^(A valid email address is required\.|Contact not found\.|Another contact already uses that email address\.|That note no longer exists\.|Pick a volunteer to share with\.|Add at least one email address\.|This contact has no email address\.|Your volunteer account is not approved yet\.|Tell us how you know these people before inviting them\. Invite mail is permission based only\.)$/i;

function shouldSendEvent(
  event: { exception?: { values?: { type?: string; value?: string }[] }; message?: string },
  hint?: { originalException?: unknown },
) {
  try {
    const original = hint?.originalException;
    // Extension / injected-script errors carry no usable frames for us.
    if (typeof original === "string" && /extension|chrome-extension/i.test(original)) return false;

    const first = event.exception?.values?.[0];
    const signature = `${first?.type ?? ""}:${first?.value ?? event.message ?? ""}`.slice(0, 300);
    const messageText = (first?.value ?? event.message ?? "").trim();
    if (EXPECTED_AUTH_ERRORS.test(messageText)) return false;
    if (EXPECTED_USER_ERRORS.test(messageText)) return false;
    if (first?.type === "UserFacingError") return false;

    if (criticalInFlight > 0) return true;

    if (seenSignatures.has(signature)) return false;


    const now = Date.now();
    if (now - windowStart > WINDOW_MS) {
      windowStart = now;
      windowCount = 0;
    }
    if (windowCount >= MAX_EVENTS_PER_WINDOW) return false;
    windowCount += 1;
    seenSignatures.add(signature);
    if (seenSignatures.size > 200) seenSignatures.clear();
    return true;
  } catch {
    return true;
  }
}

/**
 * Report an error to Sentry from the browser. No-op when Sentry is not
 * configured. Pass `{ critical: true }` in the context to bypass the dedupe
 * and rate cap for core-pipeline failures.
 */
export async function captureClientError(error: unknown, context?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (!clientSentryDsn()) return;
  await initSentryClient();
  const Sentry = await import("@sentry/react");
  const critical = context?.["critical"] === true;
  if (critical) criticalInFlight += 1;
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } finally {
    if (critical) criticalInFlight -= 1;
  }
}


/** Attach (or clear) the signed-in user so issues can be traced to a person. */
export async function setSentryUser(user: { id: string; email?: string | null } | null) {
  if (typeof window === "undefined" || !clientSentryDsn()) return;
  await initSentryClient();
  const Sentry = await import("@sentry/react");
  Sentry.setUser(user ? { id: user.id, email: user.email ?? undefined } : null);
}

/** Drop a breadcrumb for a user action so crashes carry the steps that led there. */
export async function addClientBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  category = "app",
) {
  if (typeof window === "undefined" || !clientSentryDsn()) return;
  await initSentryClient();
  const Sentry = await import("@sentry/react");
  Sentry.addBreadcrumb({ category, message, level: "info", data });
}

/**
 * Attaches durable tags to every subsequent event: the signed-in user's role,
 * the event / signup a screen is working on, and the correlation id that ties a
 * browser action to its server-side audit row.
 */
export async function setSentryTags(tags: Record<string, string | undefined | null>) {
  if (typeof window === "undefined" || !clientSentryDsn()) return;
  await initSentryClient();
  const Sentry = await import("@sentry/react");
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value == null || value === "" ? undefined : String(value));
  });
}

/** Convenience wrapper for the app role tag (admin vs volunteer vs anonymous). */
export async function setSentryRole(role: string | null) {
  await setSentryTags({ app_role: role ?? "anonymous" });
}

// Start listening immediately on import, before React hydrates.
hookEarlyErrors();

